import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { EyeOff, Loader2 } from "lucide-react";
import { api, apiErrorMessage } from "../../../lib/api";
import ScreenshotPicker, { ScreenshotWarning, uploadScreenshots } from "./ScreenshotPicker";

const PER_TICKET = 10;

/**
 * A ticket's screenshots in its drawer (14.10.2026): to look at, to add
 * more to, and — for the owner — to hide one that shows personal data.
 * The drawer loads them (`items`), with short-lived links asked for again
 * each time; those sent with a message show in the discussion instead.
 */
export default function TicketScreenshots({ ticketId, items, loading, load, isOwner = false, onChanged }) {
  const [adding, setAdding] = useState([]);
  const [busy, setBusy] = useState(false);
  const box = useRef(null);

  const live = items.filter((i) => i.status !== "hidden").length;
  const own = items.filter((i) => !i.comment_id);

  const send = async () => {
    setBusy(true);
    const failed = await uploadScreenshots(ticketId, adding);
    setBusy(false);
    if (failed < adding.length) toast.success(adding.length - failed === 1 ? "Captura a fost adăugată." : "Capturile au fost adăugate.");
    setAdding([]);
    await load();
    onChanged?.();
  };

  const hide = async (item) => {
    const reason = window.prompt(
      "Ascunzi captura: se șterge din stocare și nu mai poate fi văzută de nimeni. În tichet rămâne doar că a fost ascunsă. Motivul:",
      "date personale");
    if (reason === null) return;
    setBusy(true);
    try {
      await api.post(`/admin/tickets/${ticketId}/attachments/${item.id}/hide`, { reason });
      toast.success("Captura a fost ascunsă.");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Captura nu a putut fi ascunsă."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section ref={box} className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid="ticket-screenshots">
      <h4 className="text-xs text-[#6B7280] uppercase tracking-widest font-semibold mb-3">Capturi de ecran</h4>
      {loading ? (
        <Loader2 size={14} className="animate-spin text-[#6B7280]" />
      ) : own.length === 0 ? (
        <p className="text-sm text-[#6B7280] mb-3">Nicio captură încă.</p>
      ) : (
        <Thumbnails items={own} isOwner={isOwner} onHide={hide} busy={busy} className="mb-4" />
      )}

      {live < PER_TICKET && (
        <>
          <ScreenshotWarning />
          <ScreenshotPicker files={adding} onChange={setAdding} max={PER_TICKET - live} pasteTarget={box} />
          {adding.length > 0 && (
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => setAdding([])} disabled={busy}
                className="rounded-full px-3 py-1.5 text-xs border border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6]">
                Renunță
              </button>
              <button onClick={send} disabled={busy}
                className="rounded-full px-3 py-1.5 text-xs font-semibold bg-[#965EA7] text-white hover:bg-[#7A4986] disabled:opacity-60 inline-flex items-center gap-1.5"
                data-testid="ticket-screenshot-send">
                {busy && <Loader2 size={12} className="animate-spin" />}
                {adding.length === 1 ? "Adaugă captura" : `Adaugă ${adding.length} capturi`}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/** Screenshots as a grid of thumbnails; a hidden one says so, and why. */
export function Thumbnails({ items, isOwner = false, onHide, busy = false, className = "", small = false }) {
  const h = small ? "h-20" : "h-28";
  return (
    <ul className={`grid grid-cols-2 sm:grid-cols-3 gap-3 ${className}`}>
      {items.map((item) => (
        <li key={item.id} className="relative">
          {item.status === "hidden" ? (
            <div className={`${h} rounded-lg border border-dashed border-[#E5E7EB] bg-[#F5F6F6] text-[11px] text-[#6B7280] p-2 flex flex-col items-center justify-center text-center gap-1`}>
              <EyeOff size={16} />
              <span>Captură ascunsă{item.hidden_reason ? `: ${item.hidden_reason}` : ""}</span>
            </div>
          ) : (
            <a href={item.url} target="_blank" rel="noopener noreferrer" title={`Deschide ${item.name}`}
              className="block rounded-lg border border-[#E5E7EB] overflow-hidden hover:ring-2 hover:ring-[#965EA7]">
              <img src={item.url} alt={item.name} className={`${h} w-full object-cover bg-[#F5F6F6]`} loading="lazy" />
            </a>
          )}
          {isOwner && onHide && item.status !== "hidden" && (
            <button onClick={() => onHide(item)} disabled={busy}
              className="absolute top-1.5 right-1.5 rounded-full bg-white/90 border border-[#E5E7EB] px-2 py-0.5 text-[10px] font-semibold text-[#9b1f2c] hover:bg-white inline-flex items-center gap-1"
              data-testid={`ticket-screenshot-hide-${item.id}`}>
              <EyeOff size={11} /> Ascunde
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}
